import { useState, useEffect } from 'react';
import Modal from './Modal.jsx';

/**
 * Sprint 8 — Demande à l'utilisateur le périmètre d'une modification/suppression
 * sur une occurrence d'une série récurrente. Pattern Google Calendar / Outlook /
 * Float (cf. rapport float-allocation-recurrence-analysis.md §4.1).
 *
 * Props :
 *  - open: boolean
 *  - mode: 'edit' | 'delete' (titre adapté)
 *  - entityLabel?: 'allocation' | 'congé' (texte des options, défaut 'événement')
 *  - allowedScopes?: array de scopes autorisés (défaut: tous les 3).
 *    Permet par exemple de cacher 'this_and_future' quand on déplace une
 *    occurrence (movedToDate sans sens en split).
 *  - onConfirm(scope): callback, scope ∈ 'this_occurrence' | 'this_and_future' | 'all_occurrences'
 *  - onCancel(): callback
 */
export default function EditScopeDialog({
  open,
  mode = 'edit',
  entityLabel = 'événement',
  allowedScopes = ['this_occurrence', 'this_and_future', 'all_occurrences'],
  onConfirm,
  onCancel,
}) {
  const [scope, setScope] = useState(allowedScopes[0] ?? 'this_occurrence');

  // Réinitialise au premier scope UNIQUEMENT à l'ouverture. On ne dépend pas
  // de `allowedScopes` : c'est un paramètre par défaut (nouveau tableau à chaque
  // render), le mettre en dépendance redéclenchait l'effet à chaque re-render et
  // écrasait le choix de l'utilisateur dès qu'il cliquait un autre radio.
  useEffect(() => {
    if (open) setScope(allowedScopes[0] ?? 'this_occurrence');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const labels = {
    this_occurrence: `Uniquement cet ${entityLabel}`,
    this_and_future: `Cet ${entityLabel} et les suivants`,
    all_occurrences: `Tous les ${entityLabel}s de la série`,
  };

  const title =
    mode === 'delete'
      ? `Supprimer ${entityLabel === 'événement' ? 'cet événement récurrent' : `ce ${entityLabel} récurrent`}`
      : `Modifier ${entityLabel === 'événement' ? 'cet événement récurrent' : `ce ${entityLabel} récurrent`}`;

  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={title}
      size="sm"
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onCancel}>
            Annuler
          </button>
          <button
            type="button"
            className={mode === 'delete' ? 'btn btn-danger' : 'btn btn-primary'}
            onClick={() => onConfirm?.(scope)}
          >
            {mode === 'delete' ? 'Supprimer' : 'Continuer'}
          </button>
        </>
      }
    >
      <div role="radiogroup" aria-label="Périmètre de la modification" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {allowedScopes.map((s) => (
          <label
            key={s}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '8px 12px',
              border: '1px solid var(--glass-border)',
              borderRadius: 'var(--radius-md, 8px)',
              cursor: 'pointer',
              background: scope === s ? 'var(--glass)' : 'transparent',
            }}
          >
            <input
              type="radio"
              name="editScope"
              value={s}
              checked={scope === s}
              onChange={() => setScope(s)}
            />
            <span>{labels[s]}</span>
          </label>
        ))}
      </div>
    </Modal>
  );
}
